import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { logEvent } from '@firebase/analytics';

import { analytics } from 'config/firebase';
import { Divider, FormControl, FormLabel, TextField } from '@mui/material';
import { useUserData } from 'commons/contexts';
import { ORDER_STATUS } from 'commons/constants';
import { FIFITY_PER_CENT_TAX, HUNDRED_PER_CENT_TAX, NO_TAX } from 'commons/constants/addendum';
import { cancelOrder } from 'services/orders';

import { RadioGroup } from 'components/elements';
import { showSnackbar } from '../Snackbar/actionSnackbar';
import { CustomDialog } from '../Dialog';

import { ICancelModal } from './@types';

import * as S from './styles';

type cancelForm = {
  taxaAdendo: number;
  descricaoAdendo: string;
};

export const CancelModal = ({ isOpen, onClose, order, callback = () => {} }: ICancelModal) => {
  const { user } = useUserData();
  const [isSubmitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    taxaAdendo: NO_TAX,
    descricaoAdendo: '',
  });

  const onSubmit = async (cancelData: cancelForm) => {
    setSubmitting(true);

    await cancelOrder({ id: order.id, form: cancelData })
      .then(() => {
        showSnackbar('success', 'Pedido de figurante cancelado com sucesso!');
        logEvent(analytics, 'pedido_cancelado', {
          perfis: user.profile.code,
        });
        onClose();
        callback();
      })
      .catch((error: any) => {
        showSnackbar('error', error?.response?.data?.Message || 'Erro ao tentar cancelar pedido de figurante!');
        logEvent(analytics, 'ERROR_CANCELAR_PEDIDO', {
          resposta_backend: error?.response?.data?.Message || 'sem resposta',
        });
      });
    setSubmitting(false);
  };

  const items = [
    { label: 'Número do pedido', value: order.id, width: '100%' },
    { label: 'Data da Gravação', value: order.startRecordingDate, margin: '0 0 30px 0' },

    { label: 'Conteúdo', value: order.content },
    { label: 'Projeto', value: order.project },
    { label: 'Tarefa', value: order.task, margin: '0 0 30px 0' },

    { label: 'Centro de Custo', value: order.costCenter },
    { label: 'Finalidade', value: order.goal, margin: '0 0 30px 0' },

    { label: 'Roteiro', value: order.roadmap },
    { label: 'Pedido feito em', value: order.createAt },
    { label: 'Por', value: order.userLoggedName },
    { label: 'Hora Chegada', value: order.startRecordingTime },
    { label: 'Local', value: order.place, margin: '0 0 16px 0' },
  ];

  const itemsAreClimbed = order.items.every(item => item.status === ORDER_STATUS.CLIMBED);

  const taxOptions = [
    {
      value: NO_TAX,
      label: 'Não',
      id: 0,
    },
    {
      value: FIFITY_PER_CENT_TAX,
      label: 'Sim, de 50% (Menos de 24h para a gravação)',
      id: 1,
      disabled: !itemsAreClimbed,
    },
    {
      value: HUNDRED_PER_CENT_TAX,
      label: 'Sim, de 100% (Menos de 1h para a gravação)',
      id: 2,
      disabled: !itemsAreClimbed,
    },
  ];

  const isAvailableToCancel = !isSubmitting;

  const isAvailableToSubmit = !isSubmitting;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <CustomDialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        <S.Title>
          Deseja <S.TitleWordStrong>cancelar</S.TitleWordStrong> o pedido?
        </S.Title>
      }
      actions={[
        {
          testId: 'button-cancel',
          label: 'fechar',
          onClick: onClose,
          disabled: !isAvailableToCancel,
        },
        {
          testId: 'button-submit',
          label: 'Cancelar pedido',
          onClick: () => onSubmit(form),
          disabled: !isAvailableToSubmit,
          variant: 'outlined',
        },
      ]}
    >
      <S.Content>
        {items.map(({ label, value, margin }) => {
          return (
            <S.Row key={uuid()} $margin={margin || ''}>
              <S.Label>{label}</S.Label>
              <S.Value>{value}</S.Value>
            </S.Row>
          );
        })}
        <Divider />
        <FormControl sx={{ m: '16px 0 0 0', flexDirection: 'row' }}>
          <FormLabel sx={{ maxWidth: '150px' }}>Pagar taxa de cancelamento?</FormLabel>
          <RadioGroup
            name="taxaAdendo"
            options={taxOptions}
            value={Number(form.taxaAdendo)}
            onChange={handleChange}
            gap="0"
          />
        </FormControl>
        {!itemsAreClimbed && (
          <S.MessageError sx={{ ml: '150px' }}>
            Para adicionar a taxa de cancelamento, é necessário escalar figurantes no pedido.
          </S.MessageError>
        )}
        <TextField
          label="Observação"
          sx={{ mt: '20px' }}
          onChange={handleChange}
          value={form.descricaoAdendo}
          name={'descricaoAdendo'}
          variant="standard"
          disabled={!itemsAreClimbed}
        />
      </S.Content>
    </CustomDialog>
  );
};
